import { useEffect, useRef, type MutableRefObject } from "react";

/**
 * Shared "unsaved changes" guard used by editor screens (brands, applications,
 * price lists). Warns the user before they abandon unsaved edits.
 *
 * Coverage:
 * - Switching list items in the side list -> "Save before leaving?" confirm
 *   (OK saves then navigates, Cancel navigates discarding changes).
 * - Closing / reloading the browser tab -> native beforeunload warning.
 *
 * In-app route navigation (menu, back button) is NOT guarded: react-router's
 * useBlocker requires a data router and the console uses <BrowserRouter>.
 */

/** Resolves `true` when the save succeeded, `false` when it failed. */
export type SaveFn = () => Promise<boolean>;

export type UnsavedChangesContext = {
    isDirtyRef: MutableRefObject<boolean>;
    saveRef: MutableRefObject<SaveFn | null>;
};

/** Create the shared refs in the component that owns both the list and the editor outlet. */
export function useUnsavedChangesRefs(): UnsavedChangesContext {
    const isDirtyRef = useRef(false);
    const saveRef = useRef<SaveFn | null>(null);
    return { isDirtyRef, saveRef };
}

/**
 * Wire an editor into the guard: registers its save function and warns on
 * browser close/reload while there are unsaved changes.
 */
export function useUnsavedChangesEditor(
    context: UnsavedChangesContext | null | undefined,
    save: SaveFn | null | undefined,
) {
    const isDirtyRef = context?.isDirtyRef;
    const saveRef = context?.saveRef;

    useEffect(() => {
        if (!saveRef) return;
        saveRef.current = save ?? null;
        return () => {
            saveRef.current = null;
        };
    }, [save, saveRef]);

    useEffect(() => {
        if (!isDirtyRef) return;
        function onBeforeUnload(event: BeforeUnloadEvent) {
            if (!isDirtyRef.current) return;
            event.preventDefault();
            event.returnValue = "";
        }
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
            isDirtyRef.current = false;
        };
    }, [isDirtyRef]);
}

/**
 * Call before navigating away from an editor (e.g. clicking another list item).
 * Returns whether the navigation should proceed.
 */
export async function confirmUnsavedChanges(context: UnsavedChangesContext): Promise<boolean> {
    const { isDirtyRef, saveRef } = context;
    if (!isDirtyRef.current) return true;

    const wantsToSave = confirm("You have unsaved changes. Save before leaving?");
    if (wantsToSave) {
        const ok = await saveRef.current?.();
        if (ok === false) return false; // save failed — stay so the error is visible
    }
    isDirtyRef.current = false;
    return true;
}
